import { FC, useEffect, useRef, useState } from 'react';

interface PercentInputProps {
  /** Valor vindo do servidor, já na escala de exibição (0–100). */
  value: number;
  disabled: boolean;
  label: string;
  step?: number;
  /** Teto do campo. Passa de 100 nas faixas do semáforo — Crítico começa em 110%. */
  max?: number;
  className?: string;
  /** Recebe o texto do CAMPO — quem interpreta é a tela (clamp, fração, etc.). */
  onCommit: (valorDoCampo: string) => void;
}

/**
 * Campo de porcentagem que só confirma ao sair — e que **não deixa o servidor
 * sobrescrever o que a pessoa está digitando**.
 *
 * O `useEffect` ressincroniza o rascunho quando o número muda no servidor; sem a trava
 * de edição, esse efeito cai em cima do que foi digitado e o blur grava de volta o valor
 * ANTIGO, em silêncio. Não é hipótese: derrubou o job de testes do CI duas vezes,
 * gravando 100 depois de o teste digitar 90 — no runner lento o efeito de montagem ainda
 * estava pendente quando a digitação aconteceu, e rodou logo depois dela.
 *
 * A trava liga no `onChange` (e não no `onFocus`) de propósito: o que precisa ser
 * protegido é o texto já digitado, e um campo pode receber texto sem nunca ter recebido
 * foco — é o caso de `fireEvent.change`, e o de um autofill.
 */
const PercentInput: FC<PercentInputProps> = ({
  value,
  disabled,
  label,
  step = 1,
  max = 100,
  className,
  onCommit,
}) => {
  const doServidor = String(value);
  const [draft, setDraft] = useState(doServidor);
  const editando = useRef(false);

  useEffect(() => {
    if (!editando.current) setDraft(doServidor);
  }, [doServidor]);

  return (
    <input
      type="number"
      min={0}
      max={max}
      step={step}
      aria-label={label}
      value={draft}
      disabled={disabled}
      onChange={(e) => {
        editando.current = true;
        setDraft(e.target.value);
      }}
      onBlur={(e) => {
        editando.current = false;
        onCommit(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className={className}
    />
  );
};

export default PercentInput;
